/**
 * TodayWorklistTemplate — TEMPLATE level (Claude Design round 7, "Structured Note prototype build" ›
 * Today.dc.html). The new-design Today screen, behind `?ef=frameV2` (A24, Samer 1 Oct: "I don't want
 * the screen empty at all"). Three panes: WorklistRail · AppointmentPreview · DayAtAGlance.
 * Presentational: the page owns the data, the selection and the actions.
 *
 * Widths (round 6c table): nav 56 (the app shell) · worklist 340 · centre (fills its column; no cap) ·
 * day at a glance 380 beside the centre from 1280 (xl), collapsible to a 56 rail; below 1280 it folds
 * under the centre. No horizontal scroll at any width.
 */
import { useState } from 'react';
import { BrandLoading, EmptyState, PracticePicker, cn, type IpsSection, type PracticePickerOption } from '@scripsteam/scrips-signal-ds';
import { PageHeader } from '@/shared/ui/components/page-header';
import { HeaderTabs } from '@/shared/ui/components/header-tabs';
import type { BucketKey } from '../todays-appointments-view';
import type { DayGap } from '../lib/today-overview';
import type { TodayItem } from './today-types';
import { WorklistRail } from './WorklistRail';
import { AppointmentPreview } from './AppointmentPreview';
import { DayAtAGlance } from './DayAtAGlance';

export interface TodayWorklistTemplateProps {
  practices: PracticePickerOption[];
  selectedPracticeId: string;
  onPracticeChange: (id: string) => void;
  search: string;
  onSearch: (v: string) => void;
  activeBucket: BucketKey;
  onBucketChange: (b: BucketKey) => void;
  counts: { upcoming: number; inProgress: number; completed: number };
  /** The active bucket's appointments (the left list). */
  bucketItems: TodayItem[];
  /** Every appointment of the day, earliest first (the day pane). */
  dayItems: TodayItem[];
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  sections: IpsSection[] | null;
  sectionsLoading: boolean;
  sectionsError?: boolean;
  onMarkArrived: (id: string) => void;
  markingArrived?: boolean;
  markArrivedUnavailable?: boolean;
  onOpenVisit: (id: string) => void;
  gap: DayGap | undefined;
  dateLabel: string;
  /** Hide this page's own practice picker: embedded in /dashboard, the top bar's picker is the only one. */
  hidePracticePicker?: boolean;
}

const BUCKETS: ReadonlyArray<{ id: BucketKey; label: string; count: 'upcoming' | 'inProgress' | 'completed' }> = [
  { id: 'upcoming', label: 'Upcoming', count: 'upcoming' },
  { id: 'in-progress', label: 'In session', count: 'inProgress' },
  { id: 'completed', label: 'Completed', count: 'completed' },
];

export function TodayWorklistTemplate(p: TodayWorklistTemplateProps) {
  const [dayCollapsed, setDayCollapsed] = useState(false);
  const selected = p.dayItems.find((it) => it.id === p.selectedId);
  const ready = !p.loading && !p.error;

  const centre = p.loading ? (
    <BrandLoading label="Loading today's appointments" />
  ) : p.error ? (
    <EmptyState
      icon="warning"
      title="Couldn't load appointments"
      description="Something went wrong fetching today's schedule. Please retry."
      {...(p.onRetry ? { ctaLabel: 'Retry', onCtaClick: p.onRetry } : {})}
    />
  ) : selected ? (
    <AppointmentPreview
      item={selected}
      sections={p.sections}
      sectionsLoading={p.sectionsLoading}
      sectionsError={p.sectionsError}
      onMarkArrived={p.onMarkArrived}
      markingArrived={p.markingArrived}
      markArrivedUnavailable={p.markArrivedUnavailable}
      onOpenVisit={p.onOpenVisit}
    />
  ) : (
    <EmptyState icon="calendar" title="No appointments today" description="Booked appointments appear here." />
  );

  const day = ready && p.dayItems.length > 0 && (
    <DayAtAGlance
      items={p.dayItems}
      counts={p.counts}
      gap={p.gap}
      selectedId={p.selectedId}
      onSelect={p.onSelect}
      collapsed={dayCollapsed}
      onToggle={() => setDayCollapsed((c) => !c)}
    />
  );

  return (
    <div data-template="today-worklist" data-enc-look className="flex h-full min-w-0 flex-col">
      {/* The same page header as the default Today: the title, the buckets as underline tabs (the count in regular
          secondary ink, shown once the list has loaded), the practice picker on the right. */}
      <PageHeader
        title="Today"
        tabs={
          <HeaderTabs<BucketKey>
            tabs={BUCKETS.map((b) => ({
              id: b.id,
              label: (
                <>
                  {b.label}
                  {ready && <> <span className="header-tab-count">{p.counts[b.count]}</span></>}
                </>
              ),
            }))}
            value={p.activeBucket}
            onChange={p.onBucketChange}
            aria-label="Appointment buckets"
          />
        }
        actions={
          p.hidePracticePicker ? undefined : (
            <PracticePicker
              options={p.practices}
              value={p.selectedPracticeId}
              onChange={p.onPracticeChange}
              placeholder="Select practice"
            />
          )
        }
      />
      <div data-region="today-body" className="flex min-h-0 min-w-0 flex-1">
      <WorklistRail
        search={p.search}
        onSearch={p.onSearch}
        activeBucket={p.activeBucket}
        items={p.bucketItems}
        selectedId={p.selectedId}
        onSelect={p.onSelect}
        dateLabel={p.dateLabel}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto xl:flex-row xl:overflow-hidden">
        <main aria-label="Selected appointment" className="min-w-0 xl:flex-1 xl:overflow-y-auto">
          {/* The centre FILLS its column at every width (Samer, 1 Oct, on 1920+: "we cannot have real
              estate that empty on a product"). The 760/808 cap is the NOTE's reading measure (sheet §1b);
              it was never argued for a worklist, so it does not apply here (sheet v1.6 amendment). */}
          <div data-region="today-centre" className="flex w-full flex-col gap-4 p-6">{centre}</div>
        </main>
        {day && (
          <aside
            aria-label="Day at a glance"
            data-collapsed={dayCollapsed ? 'true' : 'false'}
            className={cn(
              'min-w-0 xl:flex-none xl:overflow-y-auto xl:border-0 xl:border-l xl:border-solid xl:border-border-card',
              dayCollapsed ? 'xl:w-14' : 'xl:w-95',
            )}
          >
            <div className="mx-auto max-w-202 xl:max-w-none">{day}</div>
          </aside>
        )}
      </div>
      </div>
    </div>
  );
}
